import React, { RefAttributes, useContext, useEffect, useState } from "react";
import {
  SettingsSectionContent,
  SettingsSectionItem,
  SettingsSectionItems,
  SettingsSectionHeader,
  SettingsSection,
} from "components/Graphs/SettingsSection";
import { OrgContext } from "contexts/OrgContext";
import CopyButton from "components/Graphs/CopyButton";
import { FlatCard, InputField } from "components/UI-lib";
import { DELETE_ORGANIZATION_HELPER_TEXT } from "constants/index";
import { Button } from "components/Graphs/Button";
import { toast } from "utils/toast";
import { useHistory, useLocation } from "react-router";
import { Link, Redirect, useParams } from "react-router-dom";
import { BuildingOfficeIcon, KeyIcon } from "@heroicons/react/24/outline";
import OrgResourceLimits from "components/OrgPages/OrgResourceLimits";
import InlineNotification from "contexts/Notifications/InlineNotifications";
import { isOrgAdminOrMaintainerRole, isOrgAdminRole } from "utils/roleUtils";
import ApplicationPageLayout from "components/ApplicationShell/ApplicationPageLayout";
import OrganizationIcon from "components/ApplicationShell/OrganizationIcon";
import SettingsScopeToggle from "components/ApplicationShell/SettingsScopeToggle";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import OrgPagePeople from "components/OrgPages/Teams";
import ManagedSecretsPage from "components/ManagedSecrets";
import OrgBillingPage from "components/OrgPages/Billing";
import {
  isApplicationShellRedesigned,
  useApplicationShellPreference,
} from "components/ApplicationShell/ApplicationShellPreference";
import {
  POSTHOG_IDENTITY_FLAGS,
  useIdentityFeatureFlagEnabled,
} from "utils/posthogFeatureFlags";
import {
  buildOrganizationSettingsSecretsHref,
  buildOrganizationSettingsTabHref,
  getOrganizationSettingsSecretIdFromSearch,
  getOrganizationSettingsTabFromSearch,
  type OrganizationSettingsTab,
} from "./settingsTabs";
import {
  applicationShellPageContentClassName,
  applicationShellPageLayoutClassName,
} from "components/ApplicationShell/pageSpacing";

const SecretDetailPage = React.lazy(
  () => import("components/ManagedSecrets/SecretDetail"),
);

const SettingsLink = ({
  active,
  icon,
  href,
  label,
}: {
  active: boolean;
  icon: React.ForwardRefExoticComponent<
    Omit<React.SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  href: string;
  label: string;
}) => {
  const IconComponent = icon;

  return (
    <div
      className={`text-sm ${
        active ? "font-bold text-highlight" : "text-gray-900 dark:text-white"
      }`}
    >
      <Link
        to={href}
        aria-current={active ? "page" : undefined}
        className="flex flex-row gap-2 items-center"
      >
        <IconComponent className="w-4 h-4" />
        {label}
      </Link>
    </div>
  );
};

export const OrgSettingsRedirect: React.FC = () => {
  const router = useHistory();
  const location = useLocation();
  const orgContext = useContext(OrgContext);

  useEffect(() => {
    if (orgContext.activeOrgId) {
      router.replace(
        `/org/${orgContext.activeOrgId}/settings${location.search}`,
      );
    }
  }, [location.search, orgContext.activeOrgId, router]);

  return <></>;
};

function OrgSettings() {
  const orgContext = useContext(OrgContext);
  const [showDeleteConfirmation, setDeleteConfirmation] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const { orgId: routeOrgId } = useParams<{ orgId?: string }>();
  const shellPreference = useApplicationShellPreference();
  const isAdmin = isOrgAdminRole(orgContext.activeOrgRole);
  const canViewResourceLimits = isOrgAdminOrMaintainerRole(
    orgContext.activeOrgRole,
  );
  const ncaId = orgContext.activeOrg?.ncaId;
  const includeSecrets =
    useIdentityFeatureFlagEnabled(POSTHOG_IDENTITY_FLAGS.BREV_SECRETS_UI) ===
    true;
  const showOrganizationTabs = isApplicationShellRedesigned(shellPreference);
  const requestedTab = getOrganizationSettingsTabFromSearch(location.search);
  const settingsSecretId = getOrganizationSettingsSecretIdFromSearch(
    location.search,
  );

  const deleteOrganization = async () => {
    setDeleting(true);
    if (orgContext.orgs.length === 1) {
      toast.error("You must be part of at least one organization", {
        description: "Create another one, and then delete this organization",
      });
      setDeleting(false);
      return;
    }
    if (orgContext.activeOrg?.id !== undefined) {
      try {
        const nextActiveOrgId = await orgContext.deleteOrg(
          orgContext.activeOrg.id,
        );
        history.push(`/org/${nextActiveOrgId}/environments`);
        setDeleteConfirmationText("");
        setDeleteConfirmation(false);
      } catch (error) {
        toast.error("Unable to delete organization", {
          description:
            error instanceof Error
              ? error.message
              : "An unexpected error occurred",
        });
      }
    }
    setDeleting(false);
  };

  if (orgContext.isLoading || !orgContext.activeOrg) {
    return <></>;
  }

  if (
    !showOrganizationTabs &&
    routeOrgId &&
    routeOrgId !== orgContext.activeOrgId
  ) {
    return (
      <Redirect
        to={buildOrganizationSettingsTabHref(
          orgContext.activeOrgId,
          requestedTab,
          location.search,
        )}
      />
    );
  }

  const availableTabs: OrganizationSettingsTab[] = [
    "settings",
    ...(isAdmin ? (["team"] as const) : []),
    ...(includeSecrets ? (["secrets"] as const) : []),
    "billing",
  ];
  const activeTab = availableTabs.includes(requestedTab)
    ? requestedTab
    : "settings";
  const legacyActiveTab =
    includeSecrets && requestedTab === "secrets" ? "secrets" : "settings";
  const secretsHref = buildOrganizationSettingsSecretsHref(
    orgContext.activeOrgId,
    location.search,
  );
  const settingsSearch = new URLSearchParams(location.search);
  settingsSearch.delete("secretId");
  const selectTab = (tab: string) => {
    history.push(
      buildOrganizationSettingsTabHref(
        orgContext.activeOrgId,
        tab as OrganizationSettingsTab,
        location.search,
      ),
    );
  };

  const settingsContent = (
    <div>
      <SettingsSection>
        <SettingsSectionContent>
          <SettingsSectionItems>
            <SettingsSectionItem
              label="Organization Name"
              value={orgContext.activeOrg.name}
            />
            <SettingsSectionItem
              label="Organization ID"
              value={orgContext.activeOrg?.id}
              showCopyButton
            />
            {ncaId && (
              <SettingsSectionItem
                label="NCA ID"
                value={ncaId}
                showCopyButton
              />
            )}
          </SettingsSectionItems>
        </SettingsSectionContent>
      </SettingsSection>
      {canViewResourceLimits && (
        <SettingsSection>
          <SettingsSectionHeader
            title="Resource Limits"
            description="Track your organization's resource usage"
          />
          <SettingsSectionContent>
            <OrgResourceLimits includeTitle={false} />
          </SettingsSectionContent>
        </SettingsSection>
      )}
      {isAdmin && (
        <SettingsSection>
          <SettingsSectionHeader
            title="Delete Organization"
            description="Delete the organization. This action cannot be undone."
            action={
              showDeleteConfirmation ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDeleteConfirmation(false);
                    setDeleteConfirmationText("");
                  }}
                >
                  Cancel
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={() => setDeleteConfirmation(true)}
                  disabled={showDeleteConfirmation || deleting}
                  isLoading={deleting}
                >
                  Delete This Organization
                </Button>
              )
            }
          />
          <SettingsSectionContent>
            {showDeleteConfirmation && (
              <FlatCard className="WorkspaceSettings-FlatCard">
                <InlineNotification
                  show={true}
                  severity="error"
                  autoClose={false}
                  hideCloseButton={true}
                  className="!bg-red-600 mb-2"
                  text="Note: Deleting this organization will result in the permanent loss of all associated credits. This cannot be undone."
                />
                <p className="mt-2 text-sm text-gray-700 dark:text-secondary">
                  {DELETE_ORGANIZATION_HELPER_TEXT}
                </p>
                <div className="mt-2 mb-2 text-sm text-gray-700 dark:text-secondary">
                  Please type{" "}
                  <span className="font-bold">{orgContext.activeOrg.name}</span>
                  <CopyButton
                    value={orgContext.activeOrg.name}
                    displayValue={<></>}
                    className="!inline-flex !gap-0 align-middle mx-1"
                  />
                  to confirm. In addition, ensure all workspaces in the
                  organization are deleted.
                </div>
                <div className="flex flex-row items-center justify-between gap-2">
                  <InputField
                    label=""
                    errorMessage=""
                    value={deleteConfirmationText}
                    onChange={(val: string) => setDeleteConfirmationText(val)}
                    className="flex-grow"
                  />
                  <div className="flex flex-row justify-end items-center">
                    <Button
                      onClick={deleteOrganization}
                      isLoading={deleting}
                      style={{ marginRight: "10px" }}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-rose-500 hover:bg-rose-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-rose-500"
                      disabled={
                        deleting ||
                        deleteConfirmationText !== orgContext.activeOrg.name
                      }
                    >
                      Delete This Organization
                    </Button>
                  </div>
                </div>
              </FlatCard>
            )}
          </SettingsSectionContent>
        </SettingsSection>
      )}
    </div>
  );
  const pageContent = showOrganizationTabs ? (
    <Tabs value={activeTab} onValueChange={selectTab}>
      <TabsList className="mb-6 overflow-x-auto">
        <TabsTrigger value="settings">Settings</TabsTrigger>
        {isAdmin && <TabsTrigger value="team">Team</TabsTrigger>}
        {includeSecrets && <TabsTrigger value="secrets">Secrets</TabsTrigger>}
        <TabsTrigger value="billing">Billing</TabsTrigger>
      </TabsList>
      <TabsContent value="settings">{settingsContent}</TabsContent>
      {isAdmin && (
        <TabsContent value="team">
          <OrgPagePeople embedded />
        </TabsContent>
      )}
      {includeSecrets && (
        <TabsContent value="secrets">
          <ManagedSecretsPage embedded />
        </TabsContent>
      )}
      <TabsContent value="billing">
        <OrgBillingPage embedded />
      </TabsContent>
    </Tabs>
  ) : (
    <div className="flex flex-col gap-6 pb-5 sm:flex-row sm:gap-2 sm:py-5">
      <nav
        aria-label="Organization settings"
        className="flex flex-col gap-3 w-40"
      >
        <SettingsLink
          active={legacyActiveTab === "settings"}
          icon={BuildingOfficeIcon}
          href={buildOrganizationSettingsTabHref(
            orgContext.activeOrgId,
            "settings",
            settingsSearch.toString(),
          )}
          label="Organization"
        />
        {includeSecrets && (
          <SettingsLink
            active={legacyActiveTab === "secrets"}
            icon={KeyIcon}
            href={secretsHref}
            label="Secrets"
          />
        )}
      </nav>
      <div className="min-w-0 flex-1">
        {legacyActiveTab === "secrets" ? (
          settingsSecretId ? (
            <React.Suspense
              fallback={
                <div className="flex min-h-80 items-center justify-center text-sm text-secondary">
                  Loading secret…
                </div>
              }
            >
              <SecretDetailPage
                embedded
                organizationId={orgContext.activeOrgId}
                selectedSecretId={settingsSecretId}
                backHref={secretsHref}
              />
            </React.Suspense>
          ) : (
            <ManagedSecretsPage embedded useSettingsDetailRoutes />
          )
        ) : (
          settingsContent
        )}
      </div>
    </div>
  );

  const content = (
    <>
      <div
        className={
          showOrganizationTabs
            ? "flex flex-wrap items-center gap-x-4 gap-y-3 pb-8"
            : "flex flex-col gap-4 pb-8 pt-10 sm:flex-row sm:items-start sm:justify-start"
        }
      >
        <h1 className="shrink-0 text-2xl font-bold text-gray-900 dark:text-white">
          {showOrganizationTabs ? "Organization Settings" : "Settings"}
        </h1>
        {showOrganizationTabs && (
          <div className="flex min-w-0 items-center gap-1.5">
            <OrganizationIcon className="size-4 shrink-0 text-gray-600 dark:text-zinc-400" />
            <p className="truncate text-sm text-gray-600 dark:text-zinc-400">
              {orgContext.activeOrg.name}
            </p>
          </div>
        )}
        <SettingsScopeToggle
          activeScope="organization"
          organizationId={orgContext.activeOrgId}
          personalLabel={showOrganizationTabs ? "Personal" : "Account"}
        />
      </div>
      {pageContent}
    </>
  );

  return (
    <ApplicationPageLayout
      className={
        showOrganizationTabs
          ? applicationShellPageLayoutClassName
          : "px-4 pb-4 sm:px-6 sm:pb-8 lg:px-8"
      }
    >
      {showOrganizationTabs ? (
        <div className={applicationShellPageContentClassName}>{content}</div>
      ) : (
        content
      )}
    </ApplicationPageLayout>
  );
}

export default OrgSettings;
